<template>
  <v-card class="add-invoice" height="90vh">
    <v-card-title>{{ $t('admin_acc_invoice.add_title') }}</v-card-title>
    <v-card-text>
      <div class="add-invoice-content" v-if="purchaseOrder">
        <div class="add-invoice-content-col left">
          <v-form ref="form">
            <v-text-field readonly
                          :label="$t('admin_acc_purchase_order.id')"
                          :value="`PO${purchaseOrder.id.toString().padStart(10, '0')}`"></v-text-field>
            <v-text-field readonly
                          :label="$t('admin_acc_purchase_order.recipient')"
                          :value="$t('name', {given_name: purchaseOrder.recipient.given_name, surname: purchaseOrder.recipient.surname})"></v-text-field>

            <v-input :rules="requiredRules"
                     v-model="invoice.issued_at">
              <v-container fluid class="flex-column pa-0">
                <legend class="v-label theme--light">{{ $t('admin_acc_invoice.issued_at') }}</legend>
                <DatePickerInput v-model="invoice.issued_at"
                                 hide-details></DatePickerInput>
              </v-container>
            </v-input>
            <v-input :rules="requiredRules"
                     v-model="invoice.due_at">
              <v-container fluid class="flex-column pa-0">
                <legend class="v-label theme--light">{{ $t('admin_acc_invoice.due_at') }}</legend>
                <DatePickerInput v-model="invoice.due_at"
                                 hide-details></DatePickerInput>
              </v-container>
            </v-input>
            <v-text-field outlined
                          :label="$t('admin_acc_invoice.title')"
                          v-model="invoice.title"
                          :rules="requiredRules"></v-text-field>
            <v-textarea outlined
                        auto-grow
                        no-resize
                        :label="$t('admin_acc_invoice.details')"
                        v-model="invoice.details"></v-textarea>
          </v-form>
        </div>
        <div class="add-invoice-content-col right">
          <v-tabs v-model="tabIndex">
            <v-tab>{{ $t('admin_acc_invoice.bookings') }}</v-tab>
            <v-tab-item>
              <AdminBookingList full-height
                                hide-actions
                                show-select
                                v-model="selectedBookingIds"
                                :extra-filters="{'purchase_order.id': purchaseOrderId}"></AdminBookingList>
            </v-tab-item>

            <v-tab>{{ $t('admin_acc_invoice.item') }}</v-tab>
            <v-tab-item>
              <InvoiceItemEditor v-model="invoice.items"
                                 :purchase-order-id="purchaseOrderId"
                                 :booking-ids="selectedBookingIds"></InvoiceItemEditor>
              <div class="invoice-items-summary mt-2">
                <div class="invoice-discount">
                  <v-text-field outlined
                                :label="$t('admin_acc_invoice.discount_percentage')"
                                suffix="%"
                                type="number"
                                step=".01"
                                min="0"
                                v-model="invoice.discount_percentage"></v-text-field>
                  <v-text-field readonly
                                :label="$t('admin_acc_invoice.discount_amount')"
                                prefix="-"
                                :value="discountAmount"></v-text-field>
                </div>
                <div class="invoice-total">
                  <v-text-field readonly
                                :label="$t('admin_acc_invoice.total')"
                                :value="invoiceTotalAfterDiscount"></v-text-field>
                </div>
              </div>
            </v-tab-item>
          </v-tabs>
        </div>
      </div>
    </v-card-text>
    <v-card-actions>
      <v-btn color="primary"
             @click="$emit('submit')">
        {{ $t('cancel') }}
      </v-btn>
      <v-spacer></v-spacer>
      <v-btn color="primary"
             @click="$refs.form.validate() && submit()">
        {{ $t('ok') }}
      </v-btn>
    </v-card-actions>
  </v-card>
</template>
<script>
import {computed, onMounted, ref} from "@vue/composition-api";
import DatePickerInput from "@/components/DatePickerInput.vue";
import InvoiceItemEditor from "@/components/acc_booking/invoice/InvoiceItemEditor.vue";
import TinyMceEditor from "@/components/TinyMceEditor.vue";
import AdminBookingList from "@/components/acc_booking/booking/AdminBookingList.vue";

export default {
  name: 'AddInvoiceModal',
  components: {AdminBookingList, TinyMceEditor, InvoiceItemEditor, DatePickerInput},
  props: {
    purchaseOrderId: [String, Number],
  },
  setup(props, {root, emit}) {
    const requiredRules = ref([
      value => !!value || root.$t('register.required_fields'),
    ]);
    const purchaseOrder = ref(null);
    const getPurchaseOrder = function () {
      root.$callAPI('aafb/admin/purchase_order/', {
        purchase_order_id: props.purchaseOrderId,
      }, (response) => {
        purchaseOrder.value = {
          ...response.body.purchase_order,
          issued_at: root.$parseDate(response.body.purchase_order.issued_at, 'yyyy-MM-dd'),
          due_at: root.$parseDate(response.body.purchase_order.due_at, 'yyyy-MM-dd'),
        };
      });
    }

    const tabIndex = ref(0);

    const invoice = ref({});
    const discountAmount = computed(() => {
      if (invoice.value.discount_percentage) {
        let p = parseFloat(invoice.value.discount_percentage) || 0;
        return invoiceTotal.value * p / 100;
      }
      return 0;
    })
    const invoiceTotal = computed(() => {
      if (invoice.value.items) {
        let s = 0;

        _.forEach(invoice.value.items, (item) => {
          let unitPrice = parseFloat(item.unit_price) || 0;
          let quantity = parseInt(item.quantity) || 1;

          s += unitPrice * quantity;
        });
        return s;
      }

      return 0;
    });
    const invoiceTotalAfterDiscount = computed(() => {
      return invoiceTotal.value - discountAmount.value;
    })

    const selectedBookingIds = ref([]);

    const submit = function () {
      let details = {
        items: invoice.value.items,
        discount_percentage: invoice.value.discount_percentage,
        booking_ids: selectedBookingIds.value,
      }

      root.$callAPI('aafb/admin/invoice/create/', {
        purchase_order_id: props.purchaseOrderId,
        ...invoice.value,
        details: JSON.stringify(details),
      }, function (response) {
        emit('submit', true);
      })
    }

    onMounted(() => {
      getPurchaseOrder()
    })

    return {
      requiredRules,
      purchaseOrder,

      tabIndex,

      invoice,
      discountAmount,
      invoiceTotalAfterDiscount,

      selectedBookingIds,

      submit,
    }
  }
}
</script>

<style lang="less">
.add-invoice {
  display: flex !important;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  .v-card__title, .v-card__text, .v-card__actions {
    width: 100%;
  }

  .v-card__title, .v-card__actions {
    flex: 0 0 auto;
  }

  .v-card__text {
    flex: 1 1 0;
    overflow: auto;
  }

  .add-invoice-content {
    width: 100%;
    height: 100%;

    display: flex;
    gap: 12px;

    .add-invoice-content-col {
      height: 100%;

      display: flex;
      flex-direction: column;
      overflow-y: auto;

      &.left {
        flex: 0 0 200px;
      }

      &.right {
        flex: 1 1 0;
      }
    }
  }

  .invoice-items-summary {
    flex: 0 0 auto;

    .invoice-discount, .invoice-total {
      display: flex;
      gap: 12px;
    }
  }

  .v-tabs {
    height: 100%;

    .v-tabs-bar {
      flex: 0 0 auto;
    }

    .v-tabs-items {
      height: calc(100% - 48px);
      flex: 1 1 0;

      .v-window__container {
        height: 100% !important;

        .v-window-item {
          height: 100% !important;
          display: flex;
          flex-direction: column;
          overflow-y: auto;
          padding-top: 12px;
        }
      }
    }
  }
}


</style>
